import { recordMonitoringMetricSample } from "..";
import type {
  ComponentMonitoringOptions,
  ComponentRenderMonitorDefinition,
  PendingComponentRenderSession,
} from "./types";

// DOM-based component monitoring that checks whether key modules finish rendering.
const DEFAULT_RENDER_TIMEOUT_MS = 5000;
const DEFAULT_COMPONENT_RENDER_METRIC_ID = "business.component_rendering_on_page";

// Counts how many success matches exist inside a component root.
function getMatchCount(root: Element, selector?: string): number {
  if (!selector) {
    return 1;
  }

  const rootMatchCount = root.matches(selector) ? 1 : 0;
  return rootMatchCount + root.querySelectorAll(selector).length;
}

// Falls back to one match when a component does not specify a stricter requirement.
function getMinimumMatchCount(component: ComponentRenderMonitorDefinition): number {
  return component.minimumMatchCount ?? 1;
}

// Decides whether the component has rendered enough success markers to pass.
function hasSuccessfulRender(session: PendingComponentRenderSession): boolean {
  return (
    getMatchCount(session.root, session.component.successSelector) >=
    getMinimumMatchCount(session.component)
  );
}

// Clears any pending timeout so a session is not recorded twice.
function clearSessionTimeout(session: PendingComponentRenderSession): void {
  if (!session.timeoutId) {
    return;
  }

  clearTimeout(session.timeoutId);
  session.timeoutId = null;
}

// Builds the component-specific attributes attached to success or failure samples.
function createComponentAttributes(
  session: PendingComponentRenderSession,
  matchCount: number
): Record<string, string | number | boolean | null> {
  return {
    component_name: session.component.componentName,
    component_root_selector: session.component.rootSelector,
    component_success_selector: session.component.successSelector ?? "",
    component_instance_index: session.componentIndex,
    component_match_count: matchCount,
    component_cmp_name: session.root.getAttribute("data-cmp"),
    component_cmp_type: session.root.getAttribute("data-cmp-type"),
    component_cmp_title: session.root.getAttribute("data-cmp-title"),
    ...session.component.meta,
  };
}

// Records a successful component render once the success condition is met.
function recordComponentRenderSuccess(
  options: ComponentMonitoringOptions,
  session: PendingComponentRenderSession
): void {
  if (session.recorded) {
    return;
  }

  session.recorded = true;
  clearSessionTimeout(session);

  const renderDurationMs = performance.now() - session.startedAt;
  const matchCount = getMatchCount(session.root, session.component.successSelector);

  recordMonitoringMetricSample(
    options.schema,
    options.metricId ?? DEFAULT_COMPONENT_RENDER_METRIC_ID,
    {
      value: 1,
      unit: "component",
      success: true,
      extra: {
        ...createComponentAttributes(session, matchCount),
        render_success: true,
        render_duration_ms: Math.round(renderDurationMs),
      },
    }
  );
}

// Records a failed component render when it times out or never stabilizes.
function recordComponentRenderFailure(
  options: ComponentMonitoringOptions,
  session: PendingComponentRenderSession,
  reason: string
): void {
  if (session.recorded) {
    return;
  }

  session.recorded = true;
  clearSessionTimeout(session);

  const renderDurationMs = performance.now() - session.startedAt;
  const matchCount = getMatchCount(session.root, session.component.successSelector);

  recordMonitoringMetricSample(
    options.schema,
    options.metricId ?? DEFAULT_COMPONENT_RENDER_METRIC_ID,
    {
      value: 1,
      unit: "component",
      success: false,
      extra: {
        ...createComponentAttributes(session, matchCount),
        render_success: false,
        render_duration_ms: Math.round(renderDurationMs),
        render_failure_reason: reason,
      },
    }
  );
}

// Creates a stable key for one observed component instance on the page.
function createSessionKey(componentName: string, root: Element, componentIndex: number): string {
  return `${componentName}::${componentIndex}::${root.tagName}`;
}

// Observes the DOM and records whether configured components render in time.
export function installComponentMonitoring(options: ComponentMonitoringOptions): () => void {
  if (typeof window === "undefined" || typeof document === "undefined" || !document.body) {
    return () => undefined;
  }

  const sessions = new Map<string, PendingComponentRenderSession>();
  const trackedRoots = new WeakSet<Element>();

  // Re-checks an existing session whenever the DOM changes.
  const evaluateSession = (session: PendingComponentRenderSession) => {
    if (session.recorded) {
      return;
    }

    if (hasSuccessfulRender(session)) {
      recordComponentRenderSuccess(options, session);
    }
  };

  // Starts tracking a new component instance the first time its root appears.
  const registerSession = (
    component: ComponentRenderMonitorDefinition,
    root: Element,
    componentIndex: number
  ) => {
    if (trackedRoots.has(root)) {
      return;
    }

    trackedRoots.add(root);

    const sessionKey = createSessionKey(component.componentName, root, componentIndex);
    const session: PendingComponentRenderSession = {
      component,
      componentIndex,
      root,
      startedAt: performance.now(),
      timeoutId: null,
      recorded: false,
    };

    const timeoutMs =
      component.renderTimeoutMs ?? options.defaultRenderTimeoutMs ?? DEFAULT_RENDER_TIMEOUT_MS;
    session.timeoutId = setTimeout(() => {
      recordComponentRenderFailure(options, session, "timeout");
      sessions.delete(sessionKey);
    }, timeoutMs);

    sessions.set(sessionKey, session);
    evaluateSession(session);
  };

  // Scans the current DOM for new component roots and updates active sessions.
  const scanComponents = () => {
    options.components.forEach((component) => {
      document.querySelectorAll(component.rootSelector).forEach((root, index) => {
        registerSession(component, root, index + 1);
      });
    });

    for (const [sessionKey, session] of sessions.entries()) {
      evaluateSession(session);

      if (session.recorded) {
        sessions.delete(sessionKey);
      }
    }
  };

  const mutationObserver = new MutationObserver(() => {
    scanComponents();
  });

  mutationObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    characterData: true,
  });

  scanComponents();

  return () => {
    mutationObserver.disconnect();

    for (const session of sessions.values()) {
      clearSessionTimeout(session);
    }

    sessions.clear();
  };
}

export type { ComponentMonitoringOptions, ComponentRenderMonitorDefinition } from "./types";
